import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { motion } from "framer-motion";
import { Download, Linkedin, Check } from "lucide-react";

const BAND_COLOUR = {
  Aligned: "#E86A20",
  // Darker slate so the badge remains legible against the linen background.
  Divergent: "#3B4A6B",
  Disconnected: "#B23A1A",
};

function bandFill(label) {
  return BAND_COLOUR[label] || "#0E214810";
}

export default function AdminCohort() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/admin/directorate-cohort")
      .then(({ data }) => setData(data))
      .catch((e) => setError(e.response?.data?.detail || e.message));
  }, []);

  if (error) return <div className="tick-label text-heritage" data-testid="cohort-error">{error}</div>;
  if (!data) return <div className="tick-label">Loading cohort data…</div>;

  const { weeks, sessions, session_count, sample_count, weekly_aggregate } = data;

  if (!weeks || weeks.length === 0 || session_count === 0) {
    return (
      <div className="border border-oxford/15 bg-linen p-10 text-center" data-testid="cohort-empty">
        <div className="tick-label-accent mb-3">No cohort data yet</div>
        <p className="text-charcoal max-w-lg mx-auto">
          Once at least one directorate has been snapshotted (either automatically on Monday, or
          manually via <em>Run snapshot now</em> below), the cohort view will populate.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-10" data-testid="cohort-view">
      {/* Hero paragraph */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="display-serif text-3xl md:text-4xl text-oxford">
            Every organisation, on one page.
          </h2>
          <p className="mt-3 max-w-3xl text-charcoal leading-relaxed">
            A cross-cohort read of the client base — each row is one organisation's directorates
            moving through the same {weeks.length} weeks. Look for shared patterns: quarters where
            many boards drifted, industries where alignment holds, and the outliers that recovered.
          </p>
          <div className="tick-label mt-3">
            {session_count} organisation{session_count === 1 ? "" : "s"} · {sample_count} snapshots ·{" "}
            {weeks.length} weeks
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <LinkedInShareButton />
          <a
            href={`${API}/admin/export/cohort.csv`}
            target="_blank"
            rel="noreferrer"
            className="btn-ghost"
            data-testid="cohort-export-csv"
          >
            <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
            Cohort CSV
          </a>
        </div>
      </div>

      {/* Weekly aggregate strip */}
      <div className="border border-oxford/15 bg-linen p-6" data-testid="cohort-weekly-aggregate">
        <div className="tick-label-accent mb-4">Cohort median · weekly</div>
        <div className="flex items-end gap-2 h-32">
          {weekly_aggregate.map((w) => {
            const total = (w.labels.Aligned || 0) + (w.labels.Divergent || 0) + (w.labels.Disconnected || 0) || 1;
            const alignedPct = ((w.labels.Aligned || 0) / total) * 100;
            const divergentPct = ((w.labels.Divergent || 0) / total) * 100;
            const disconnectedPct = ((w.labels.Disconnected || 0) / total) * 100;
            return (
              <div key={w.week} className="flex-1 min-w-0 flex flex-col items-center gap-1 h-full">
                <div
                  title={`${w.week} · median VRI ${w.median_blended_vri ?? "—"} · n=${w.n}`}
                  className="w-full flex flex-col justify-end flex-1"
                >
                  <div style={{ background: BAND_COLOUR.Disconnected, height: `${disconnectedPct}%` }} />
                  <div style={{ background: BAND_COLOUR.Divergent, height: `${divergentPct}%` }} />
                  <div style={{ background: BAND_COLOUR.Aligned, height: `${alignedPct}%` }} />
                </div>
                <div className="tick-label text-[9px] mt-1 text-center w-full truncate">
                  {w.week.replace(/^\d{4}-/, "")}
                </div>
                <div className="font-mono text-[10px] text-oxford">{w.median_blended_vri ?? "—"}</div>
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-6 mt-4 text-sm">
          <Legend colour={BAND_COLOUR.Aligned} label="Aligned" />
          <Legend colour={BAND_COLOUR.Divergent} label="Divergent" />
          <Legend colour={BAND_COLOUR.Disconnected} label="Disconnected" />
        </div>
      </div>

      {/* Sessions list */}
      <div className="space-y-6">
        {sessions.map((s, sIdx) => (
          <motion.div
            key={s.code}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: sIdx * 0.05 }}
            className="border border-oxford/15 bg-linen p-6"
            data-testid={`cohort-session-${sIdx}`}
          >
            <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
              <div>
                <div className="tick-label-accent">{s.code}</div>
                <div className="display-serif text-2xl text-oxford mt-1">{s.organisation}</div>
              </div>
              <div className="tick-label">
                {s.directorate_count} directorate{s.directorate_count === 1 ? "" : "s"}
              </div>
            </div>

            {/* Per-directorate mini-timeline */}
            <div className="space-y-3">
              {s.directorates.map((d, dIdx) => (
                <div key={d.member_id} className="grid grid-cols-12 gap-4 items-center" data-testid={`cohort-directorate-${sIdx}-${dIdx}`}>
                  <div className="col-span-3">
                    <div className="text-oxford text-sm">{d.name}</div>
                    <div className="tick-label text-[10px] mt-0.5">{d.role || "Director"}</div>
                  </div>
                  <div className="col-span-6 flex gap-1 items-end">
                    {weeks.map((wk, i) => {
                      const pt = d.series[i];
                      const colour = pt ? bandFill(pt.cascade_label) : "#0E214810";
                      const height = pt ? Math.max(10, Math.min(28, (pt.blended_vri / 125) * 28)) : 10;
                      return (
                        <div
                          key={wk}
                          title={pt ? `${wk} · VRI ${pt.blended_vri} · ${pt.cascade_label}` : `${wk} · —`}
                          className="flex-1 min-w-0"
                          style={{ background: colour, height: `${height}px` }}
                        />
                      );
                    })}
                  </div>
                  <div className="col-span-2 text-right">
                    {d.band_change ? (
                      <div className="font-mono text-xs">
                        <span style={{ color: bandFill(d.band_change.from) }}>{d.band_change.from}</span>
                        <span className="text-oxford/40 mx-1">→</span>
                        <span style={{ color: bandFill(d.band_change.to) }}>{d.band_change.to}</span>
                      </div>
                    ) : (
                      <span className="tick-label">— steady —</span>
                    )}
                  </div>
                  <div className="col-span-1 text-right font-mono text-oxford">
                    {d.current ? d.current.blended_vri : "—"}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function Legend({ colour, label }) {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-block w-3 h-3" style={{ background: colour }} />
      <span className="text-oxford">{label}</span>
    </div>
  );
}

function LinkedInShareButton() {
  const [state, setState] = useState("idle"); // idle | loading | ready | error
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState(null); // {caption, share_url}

  const handleClick = async () => {
    if (state === "loading") return;
    setState("loading");
    setMessage("");
    try {
      const { data } = await api.get("/admin/annual-index-share");
      // Copy caption to clipboard
      let copied = false;
      try {
        await navigator.clipboard.writeText(data.caption);
        copied = true;
      } catch {
        copied = false;
      }
      // Open LinkedIn share dialog (LinkedIn scrapes the /annual-index OG tags for the image)
      window.open(data.share_url, "_blank", "noopener,noreferrer,width=720,height=680");
      setPreview({ caption: data.caption, share_url: data.share_url });
      setState("ready");
      setMessage(
        copied
          ? "Caption copied to clipboard. Paste it into the LinkedIn window that just opened, then hit Post."
          : "LinkedIn window opened. Copy the caption below and paste it in.",
      );
    } catch (err) {
      setState("error");
      setMessage(err.response?.data?.detail || "Could not prepare the share payload.");
    }
  };

  return (
    <>
      <button
        onClick={handleClick}
        disabled={state === "loading"}
        className="btn-ghost"
        data-testid="linkedin-share-button"
      >
        {state === "ready" ? (
          <>
            <Check className="w-3.5 h-3.5" strokeWidth={1.5} /> LinkedIn opened
          </>
        ) : (
          <>
            <Linkedin className="w-3.5 h-3.5" strokeWidth={1.5} />
            {state === "loading" ? "Preparing…" : "Post to LinkedIn"}
          </>
        )}
      </button>
      {message && (
        <div className="w-full mt-3 border border-oxford/15 bg-linen shadow-sm p-4 basis-full order-last" data-testid="linkedin-share-panel">
          <div className={`text-sm ${state === "error" ? "text-heritage" : "text-oxford"}`}>
            {message}
          </div>
          {preview && (
            <>
              <div className="tick-label-accent mt-4 mb-2">Caption preview</div>
              <textarea
                readOnly
                value={preview.caption}
                onFocus={(e) => e.target.select()}
                className="w-full h-40 border border-oxford/15 bg-parchment p-3 text-xs font-mono text-oxford resize-none focus:outline-none focus:border-orange"
                data-testid="linkedin-share-caption"
              />
              <div className="flex gap-3 mt-3">
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(preview.caption);
                      setMessage("Caption copied again. Paste into LinkedIn.");
                    } catch {
                      setMessage("Clipboard blocked — select the text manually.");
                    }
                  }}
                  className="btn-ghost text-[10px] px-3 py-2"
                  data-testid="linkedin-share-copy"
                >
                  Copy again
                </button>
                <a
                  href={preview.share_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost text-[10px] px-3 py-2"
                  data-testid="linkedin-share-reopen"
                >
                  Re-open LinkedIn
                </a>
                <button
                  onClick={() => { setPreview(null); setMessage(""); setState("idle"); }}
                  className="btn-ghost text-[10px] px-3 py-2"
                  data-testid="linkedin-share-dismiss"
                >
                  Dismiss
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

